/**
 * The typed load-failure model (architecture.md §6).
 *
 * @remarks
 * Load failures are data, not exceptions: every way a bundle can fail to load
 * is a discriminated-union variant with actionable fields. These are the same
 * errors the future creator studio surfaces to authors, so the messages are
 * written for humans from day one.
 *
 * @packageDocumentation
 */

/** A cross-reference names a target that does not exist in the pack. */
export interface ReferenceBrokenFailure {
	/** Human-readable explanation, author-facing. */
	readonly message: string;
	/** JSON-path-ish locator of the referring field (e.g. `rooms[2].exits[0].targetRoomId`). */
	readonly referencePath: string;
	/** The id the reference pointed at. */
	readonly targetId: string;
	readonly type: 'reference_broken';
}

/**
 * The pack's merged vocabulary was rejected by the parser's `compileLexicon`
 * (R3 ruling, 2026-08-13). A distinct variant from `schema_invalid` on
 * purpose: the document SATISFIED the schemas — the language it authored is
 * what's wrong, and the creator studio words those two problems differently.
 */
export interface LexiconInvalidFailure {
	/** The compiler's issue lines, verbatim (`compileLexicon` is the single semantic authority — D14). */
	readonly issues: readonly string[];
	/** Human-readable summary, author-facing. */
	readonly message: string;
	readonly type: 'lexicon_invalid';
}

/** The bundle bytes do not satisfy the content schemas. */
export interface SchemaInvalidFailure {
	/** Individual validation issues, each with its path (author-facing). */
	readonly issues: readonly string[];
	/** Human-readable summary, author-facing. */
	readonly message: string;
	readonly type: 'schema_invalid';
}

/** The bundle source could not be read at all. */
export interface SourceUnreachableFailure {
	/** Human-readable explanation, author-facing. */
	readonly message: string;
	/** Which source failed (e.g. `local JSON import`, a URL, a preview endpoint id). */
	readonly sourceDescription: string;
	readonly type: 'source_unreachable';
}

/** The bundle declares a schema version this loader does not support. */
export interface VersionUnsupportedFailure {
	/** Human-readable explanation, author-facing. */
	readonly message: string;
	/** The version the bundle declared. */
	readonly requestedVersion: string;
	/** The versions this loader accepts. */
	readonly supportedVersions: readonly string[];
	readonly type: 'version_unsupported';
}

/** Every way a bundle can fail to load. */
export type LoadFailure =
	| LexiconInvalidFailure
	| ReferenceBrokenFailure
	| SchemaInvalidFailure
	| SourceUnreachableFailure
	| VersionUnsupportedFailure;

/** The discriminant values of {@link LoadFailure}, for guards and exhaustive tooling. */
export const LOAD_FAILURE_TYPES = [
	'lexicon_invalid',
	'reference_broken',
	'schema_invalid',
	'source_unreachable',
	'version_unsupported',
] as const;

/**
 * Type guard: is this value a {@link LoadFailure}?
 *
 * @param value - Any value (typically an `unknown` from a catch or a boundary)
 * @returns Whether `value` carries a known load-failure discriminant
 */
export const isLoadFailure = (value: unknown): value is LoadFailure => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const candidateType = (value as { readonly type?: unknown }).type;

	return typeof candidateType === 'string' && (LOAD_FAILURE_TYPES as readonly string[]).includes(candidateType);
};

/**
 * Render one load failure as a single human-readable line (log/CLI-facing;
 * structured consumers should read the fields instead).
 *
 * @remarks
 * D135 (Calder's item-seven doc §5): the counting variants used to print the
 * COUNT of their issues and drop the issues — an author who typoed a
 * vocabulary field was told the vocabulary was rejected and not one word
 * about which field or why, while the compiler's precise sentence sat in the
 * failure unread. The issues are the diagnostic; the count is a header.
 *
 * @param failure - The failure to describe
 * @returns A one-line description
 */
export const describeLoadFailure = (failure: LoadFailure): string => {
	switch (failure.type) {
		case 'lexicon_invalid':
			return `Vocabulary failed to compile ${countedIssues(failure.issues)}: ${failure.message} — ${failure.issues.join('; ')}`;
		case 'reference_broken':
			// D109 (Calder's R31 §5 nit): the family long ago outgrew "no
			// content with id" — duplicates, dead content, two-homes, and the
			// geometry checks all report through it, and a good diagnostic
			// wrapped in a wrong one trains authors to distrust the check. The
			// MESSAGE carries the specifics; the frame stays neutral.
			return `Content defect at ${failure.referencePath}, concerning "${failure.targetId}": ${failure.message}`;
		case 'schema_invalid':
			return `Schema validation failed ${countedIssues(failure.issues)}: ${failure.message} — ${failure.issues.join('; ')}`;
		case 'source_unreachable':
			return `Bundle source unreachable (${failure.sourceDescription}): ${failure.message}`;
		case 'version_unsupported':
			return `Unsupported schema version "${failure.requestedVersion}" (supported: ${failure.supportedVersions.join(', ')}): ${failure.message}`;
	}
};

/**
 * The `(<count> issue<s>)` header every counting variant shares.
 *
 * @param issues - The failure's issues
 * @returns The parenthesized count
 */
const countedIssues = (issues: readonly string[]): string => `(${String(issues.length)} issue${issues.length === 1 ? '' : 's'})`;
